import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getProjectPid } from "../../utils/sessionProject";
import {
  Mail, ShieldCheck, User, UserRound,
  Save, CheckCircle2,
} from "lucide-react";
import { getUser, updateUser, createUser } from "../../services/userService";
import { toast } from 'react-toastify';
import CustomSelect from "../../components/CustomSelect";

const AVATAR_COLORS = [
  "linear-gradient(135deg, var(--primaryColor), var(--primaryLight))",
  "linear-gradient(135deg, #7c3aed, #a855f7)",
  "linear-gradient(135deg, #059669, #34d399)",
  "linear-gradient(135deg, #d97706, #fbbf24)",
  "linear-gradient(135deg, #dc2626, #f87171)",
  "linear-gradient(135deg, #0891b2, #22d3ee)",
  "linear-gradient(135deg, #be185d, #ec4899)",
  "linear-gradient(135deg, #65a30d, #a3e635)",
];

const ROLE_OPTIONS = [
  { value: "project_admin", label: "Project Admin" },
  { value: "manager",       label: "Manager" },
];

const ROLE_META = {
  superadmin: { label: "Super Admin", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  admin:      { label: "Admin",       color: "var(--primaryColor)", bg: "var(--primaryTint)", border: "var(--primaryTintBorder)" },
  manager:    { label: "Manager",     color: "#059669", bg: "#f0fdf4", border: "#bbf7d0" },
};

const getInitials = (first, last) =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();

const getAvatarGradient = (id) =>
  AVATAR_COLORS[(id ?? 0) % AVATAR_COLORS.length];

const defaultUser = {
  id: null, firstName: "", lastName: "",
  email: "", role: "manager", avatar: null, createdAt: "",
};

const UserDetails = () => {
  const { state } = useLocation();
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const isNew = urlId === "new";
  // userPid is only valid when not "new"
  const userPid = isNew ? null : urlId;

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", role: "manager",
  });
  const [incoming, setIncoming] = useState(isNew ? defaultUser : (state?.user ?? defaultUser));
  const [imgError, setImgError] = useState(false);
  const [saved, setSaved]       = useState(false);
  const [editMode, setEditMode] = useState(isNew);
  const [loading, setLoading]   = useState(!isNew);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    if (isNew) {
      setForm({ firstName: "", lastName: "", email: "", role: "manager", dob: "", gender: "male" });
      setIncoming(defaultUser);
      setLoading(false);
      return;
    }

    // If user data passed via navigation state, use it directly
    if (state?.user) {
      const u = state.user;
      setIncoming(u);
      setForm({
        firstName: u.first_name || u.firstName || "",
        lastName:  u.last_name  || u.lastName  || "",
        email:     u.email      || "",
        role:      u.role       || "manager",
      });
      setLoading(false);
      return;
    }

    // No state passed — fetch from API using userPid from URL
    const fetchUser = async () => {
      setLoading(true);
      try {
        const res = await getUser(userPid);
        const u = res.data?.response?.user || res.data?.response || {};
        setIncoming(u);
        setForm({
          firstName: u.first_name || u.firstName || "",
          lastName:  u.last_name  || u.lastName  || "",
          email:     u.email      || "",
          role:      u.role       || "manager",
        });
      } catch {
        setError("Failed to load user.");
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userPid, isNew, state]);

  const roleMeta    = ROLE_META[form.role] ?? ROLE_META.manager;
  const showAvatar  = incoming.avatar && !imgError;
  const displayName = (form.firstName || form.lastName)
    ? `${form.firstName} ${form.lastName}`.trim()
    : "New User";

  const handleChange = (key, val) => {
    setForm(p => ({ ...p, [key]: val }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setError("");
    try {
      if (isNew) {
        const projectPid = getProjectPid();
        await createUser({ projectPid, firstName: form.firstName, lastName: form.lastName, email: form.email, role: form.role });
        toast.success("User created successfully!");
        navigate('/user-list');
        return;
      }
      await updateUser(userPid, { firstName: form.firstName, lastName: form.lastName, gender: form.gender, dob: form.dob, role: form.role });
      setEditMode(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save changes.");
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) return <main className="app-wrapper ud3-page"><div className="ud3-wrap" style={{paddingTop:60,textAlign:'center',color:'#94a3b8'}}>Loading...</div></main>;

  return (
    <main className="app-wrapper ud3-page">
      <div className="ud3-wrap">

        {/* ── Top Bar ── */}
        <div className="ud3-topbar">
          <div className="ud3-topbar-user">
            <div className="ud3-tb-avatar-wrap">
              <div
                className="ud3-tb-avatar"
                style={{ background: showAvatar ? "transparent" : getAvatarGradient(incoming.id) }}
              >
                {showAvatar
                  ? <img src={incoming.avatar} alt={incoming.firstName} className="prf3-avatar-img" onError={() => setImgError(true)} />
                  : <span className="ud3-tb-initials">{isNew ? <UserRound size={24} color="#fff" /> : getInitials(form.firstName, form.lastName)}</span>
                }
              </div>
              <span className="prf3-status-dot" />
            </div>
            <div>
              <p className="ud3-tb-name">{displayName}</p>
              <p className="ud3-tb-role">
                <span style={{ color: roleMeta.color, background: roleMeta.bg, border: `1px solid ${roleMeta.border}`, padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
                  {roleMeta.label}
                </span>
                {form.email && <span style={{ marginLeft: 8, color: "#94a3b8", fontSize: 12 }}>{form.email}</span>}
              </p>
            </div>
          </div>
        </div>

        {/* ── Form Panel ── */}
        <div className="ud3-main">
          <div className="ud3-form-card">
            <div className="ud3-form-head">
              <div className="ud3-form-head-left">
                <div className="ud3-form-ico"><User size={17} /></div>
                <div>
                  <h2 className="ud3-form-title">{isNew ? "Create New User" : "User Information"}</h2>
                  <p className="ud3-form-sub">{isNew ? "Fill in the details below to add a new user" : "View and edit user information"}</p>
                </div>
              </div>
              {!isNew && !editMode && (
                <button className="ud3-btn-outline" onClick={() => setEditMode(true)}>Edit User</button>
              )}
            </div>

            <div className="ud3-form-body">
              {!editMode && !isNew ? (
                /* ── View mode ── */
                <div className="ud3-view-grid">
                  {[
                    { icon: User,        label: "First Name", val: form.firstName },
                    { icon: User,        label: "Last Name",  val: form.lastName  },
                    { icon: Mail,        label: "Email",      val: form.email     },
                    { icon: ShieldCheck, label: "Role",       val: roleMeta.label },
                  ].map(({ icon: Icon, label, val }) => (
                    <div key={label} className="ud3-view-item">
                      <div className="ud3-view-ico"><Icon size={13} /></div>
                      <div className="ud3-view-content">
                        <span className="ud3-view-lbl">{label}</span>
                        <span className="ud3-view-val">{val || "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* ── Edit / Create mode ── */
                <div className="ud3-grid">
                  <div className="ud3-field">
                    <label className="ud3-label">First Name</label>
                    <input className="ud3-input" type="text" placeholder="Enter first name" value={form.firstName} onChange={e => handleChange("firstName", e.target.value)} />
                  </div>
                  <div className="ud3-field">
                    <label className="ud3-label">Last Name</label>
                    <input className="ud3-input" type="text" placeholder="Enter last name" value={form.lastName} onChange={e => handleChange("lastName", e.target.value)} />
                  </div>
                  <div className="ud3-field ud3-field-full">
                    <label className="ud3-label">Email Address</label>
                    <div className="ud3-input-wrap">
                      <Mail size={15} className="ud3-input-ico" />
                      <input
                        className="ud3-input ud3-input-pl"
                        type="email"
                        placeholder="user@example.com"
                        value={form.email}
                        onChange={e => handleChange("email", e.target.value)}
                        readOnly={!isNew}
                        style={!isNew ? { background: "#f8fafc", color: "#94a3b8", cursor: "not-allowed" } : undefined}
                        title={!isNew ? "Email cannot be changed" : undefined}
                      />
                    </div>
                  </div>
                  <div className="ud3-field">
                    <label className="ud3-label">Role</label>
                    <CustomSelect
                      value={form.role}
                      onChange={val => handleChange("role", val)}
                      options={ROLE_OPTIONS}
                    />
                  </div>
                  {error && <p style={{color:'#dc2626',fontSize:13,margin:'4px 0 0'}}>{error}</p>}
                  <div className="ud3-save-bar ud3-field-full">
                    <button className="ud3-btn-ghost" onClick={() => isNew ? navigate(-1) : setEditMode(false)}>Cancel</button>
                    <button className={`ud3-btn-primary${saved ? " saved" : ""}`} onClick={handleSave} disabled={saveLoading}>
                      {saved ? <><CheckCircle2 size={15} /> Saved!</> : saveLoading ? "Saving..." : <><Save size={15} /> {isNew ? "Create User" : "Save Changes"}</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </main>
  );
};

export default UserDetails;
